import { useRender } from '@base-ui/react/use-render'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { textVariants } from './text.variants.ts'

/**
 * Text renders body copy in the body typeface.
 *
 * `variant` picks the Figma body style (`label`/`body1`/`body2`/`caption`). Figma's paired
 * `… Semibold` styles are expressed as `weight="semibold"` rather than separate variants. Defaults to a
 * `<span>`; pass `render` (e.g. `render={<p />}`) to change the element.
 *
 * **Context:** sizes resolve to the **BO** skin by default; wrap a subtree in `[data-typescale="fo"]`
 * to switch to the responsive FO scale (Mobile <1024px, Desktop ≥1024px). `font-body` resolves per
 * active theme; Body stays Inter in both contexts.
 *
 * @summary Body copy, `label`/`body1`/`body2`/`caption` variants with a `weight` override.
 */
function Text(props: Text.Props) {
  const { className, variant = 'body1', weight, render, ref, ...rest } = props
  return useRender({
    render,
    ref,
    defaultTagName: 'span',
    props: {
      'data-slot': 'text',
      className: cn(textVariants({ variant, weight }), className),
      ...rest,
    },
  })
}

namespace Text {
  type Variants = NonNullable<VariantProps<typeof textVariants>>
  export type Props = useRender.ComponentProps<'span'> & {
    /** Body style (Figma style name). @default 'body1' */
    variant?: NonNullable<Variants['variant']> | undefined
    /** Weight override; omit to keep the variant's baseline. @default undefined */
    weight?: NonNullable<Variants['weight']> | undefined
  }
}

export { Text, textVariants }
